import {
  Schema,
  SchemaInstance,
} from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { MarketingTeam } from "@redotech/redo-marketing-core-common/marketing/marketing-team-zod";
import { UtmParameters } from "@redotech/redo-marketing-core-common/utm";
import {
  EmailFormat,
  EmailRenderEnvironment,
} from "@redotech/redo-marketing-templates-common/email-builder";
import {
  GetEmailRecommendedInteractiveProductsParams,
  InteractiveCartProduct,
} from "@redotech/redo-marketing-templates-common/email-product-recommendation";
import { EmailTemplate } from "@redotech/redo-marketing-templates-common/email-template";
import { createContext } from "react";

/**
 * This context is accessible in all email components, whether in the builder or in production emails.
 */
export type EmailRenderContext = {
  team: MarketingTeam;
  template: EmailTemplate;
  schemaInstance: SchemaInstance<Schema>;
  utm: UtmParameters;
  /**
   * A reference to the email's own S3 URL.
   * This is so interactive components can display a fallback that links to viewing the same email in the browser.
   * The only difference between the browser view and the email itself is that, in the browser view, emailS3Url is undefined.
   */
  externalFallbackUrl?: string;
  /**
   * A unique identifier for the single email "instance" being sent to a single email address.
   */
  emailId: string;
  automationId?: string;
  automationExecutionId?: string;
  campaignId?: string;
  recipient: {
    firstName?: string;
    lastName?: string;
    redoCustomerId?: string;
    email: string;
  };
  format: EmailFormat;
  forceFallback: boolean;
  /** Force rendering of data attributes for click tracking, even in non-BUILDER environments */
  forceClickMapDataAttributes?: boolean;
  /**
   * **FOR TESTING ONLY** - The width at which the email is being rendered, used for percentage calculations.
   *
   * **WARNING**: DO NOT set this in production code. Production emails must always render at
   * EMAIL_MAX_WIDTH_PX (600) to ensure proper responsive behavior across all email clients.
   *
   * This field exists solely for visual regression tests to match editor preview padding
   * at different viewport widths. Misuse will break responsive email rendering.
   *
   * Defaults to EMAIL_MAX_WIDTH_PX (600) if not specified.
   */
  renderWidth?: number;
  /**
   * Optional logger for reporting warnings when blocks encounter "impossible" states.
   */
  logger?: { warn: (message: string, context?: object) => void };
  locale?: string;
  translate?: (key: string, options?: Record<string, unknown>) => string;
} & (
  | {
      environment: EmailRenderEnvironment.BUILDER;
      builderContext: {
        getInteractiveProductRecommendations(
          params: GetEmailRecommendedInteractiveProductsParams,
        ): Promise<InteractiveCartProduct[]>;
      };
    }
  | { environment: EmailRenderEnvironment.PREVIEW }
  | { environment: EmailRenderEnvironment.SEED_LIST }
  | { environment: EmailRenderEnvironment.REAL }
  | { environment: EmailRenderEnvironment.VIEW_IN_BROWSER }
);

export const EmailRenderContext = createContext<EmailRenderContext | undefined>(
  undefined,
);
